import {
  UseQueryResult,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { CustomerLiteModel } from 'api-hooks/customer';
import { QueryFetchFunction, QueryTransformer } from 'common/helpers/common';
import {
  ApiError,
  ApiResult,
  ExtendedApiResult,
} from 'common/repositories/common.model';

import {
  getGuestsInput,
  getGuestInput,
  GuestLiteModel,
  GuestModel,
  GuestListModel,
  getGuestCustomersInput,
  GuestGetSaleTotalModel,
  TopModel,
  UpcomingModel,
  GuestCustomerRelationModel,
  LastTransactionModel,
  TopBookingModel,
  GuestRelationModel,
} from './model';

export function useGetGuestLists(
  input?: getGuestsInput,
  options?: UseQueryOptions<ExtendedApiResult<GuestListModel[]>, ApiError>,
): UseQueryResult<ExtendedApiResult<GuestListModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['getGuestLists', input?.params],
      () =>
        QueryFetchFunction({
          url: 'guests/list',
          params: input?.params,
        }),
      options,
    ),
    GuestListModel,
  );
}

export function useGetGuests(
  input?: getGuestsInput,
  options?: UseQueryOptions<ExtendedApiResult<GuestLiteModel[]>, ApiError>,
): UseQueryResult<ExtendedApiResult<GuestLiteModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['getGuests', input?.params],
      () =>
        QueryFetchFunction({
          url: 'guests',
          params: input?.params,
        }),
      options,
    ),
    GuestLiteModel,
  );
}

export function useGetGuest(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<GuestModel>, ApiError>,
): UseQueryResult<ApiResult<GuestModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['getGuest', input?.id],
      () => QueryFetchFunction({ url: `guests/${input?.id}` }),
      options,
    ),
    GuestModel,
  );
}

export function useGetGuestTable(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<GuestModel>, ApiError>,
): UseQueryResult<ApiResult<GuestModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-guest-table', input?.id],
      () => QueryFetchFunction({ url: `guests/${input?.id}` }),
      options,
    ),
    GuestModel,
  );
}
export function useGetGuestCustomers(
  input?: getGuestCustomersInput,
  options?: UseQueryOptions<ExtendedApiResult<CustomerLiteModel[]>, ApiError>,
): UseQueryResult<ExtendedApiResult<CustomerLiteModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['getGuestCustomers', input],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/customers`,
          params: input?.params,
        }),
      options,
    ),
    CustomerLiteModel,
  );
}

export function useGetGuestSaleTotal(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<GuestGetSaleTotalModel[]>, ApiError>,
): UseQueryResult<ApiResult<GuestGetSaleTotalModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-guest-sale-total', input],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-sale-total-per-type`,
          params: {
            month_limit: input?.month_limit,
          },
        }),
      options,
    ),
    GuestGetSaleTotalModel,
  );
}

export function useGetGuestTopFrequency(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<TopModel>, ApiError>,
): UseQueryResult<ApiResult<TopModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-tops', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-tops`,
        }),
      options,
    ),
    TopModel,
  );
}

export function useGetGuestCustomerRelations(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<GuestCustomerRelationModel[]>, ApiError>,
): UseQueryResult<ApiResult<GuestCustomerRelationModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-customer-relations', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-customer-relations`,
        }),
      options,
    ),
    GuestCustomerRelationModel,
  );
}

export function useGetGuestUpcomingTrips(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<UpcomingModel[]>, ApiError>,
): UseQueryResult<ApiResult<UpcomingModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-upcoming', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-upcoming-trips`,
        }),
      options,
    ),
    UpcomingModel,
  );
}

export function useGetGuestLastTransactions(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<LastTransactionModel[]>, ApiError>,
): UseQueryResult<ApiResult<LastTransactionModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-latest-transactions', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-latest-transactions`,
        }),
      options,
    ),
    LastTransactionModel,
  );
}

export function useGetGuestTopBookingPreferences(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<TopBookingModel[]>, ApiError>,
): UseQueryResult<ApiResult<TopBookingModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-top-booking-preferences', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-top-booking-preferences`,
        }),
      options,
    ),
    TopBookingModel,
  );
}

export function useGetGuestRelationType(
  input?: getGuestInput,
  options?: UseQueryOptions<ApiResult<GuestRelationModel[]>, ApiError>,
): UseQueryResult<ApiResult<GuestRelationModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-guest-relation-type', input?.id],
      () =>
        QueryFetchFunction({
          url: `guests/${input?.id}/get-guest-relations`,
        }),
      options,
    ),
    GuestRelationModel,
  );
}

export function getGuestListsKey(input?: getGuestsInput) {
  return ['getGuestLists', input?.params];
}

export function getGuestsKey(input?: getGuestsInput) {
  return ['getGuests', input?.params];
}

export function getGuestKey(input?: getGuestInput) {
  return ['getGuest', input?.id];
}

export function getGuestCustomersKey(input?: getGuestCustomersInput) {
  return ['getGuestCustomers', input];
}

export function getGuestSaleTotalKey(input?: getGuestInput) {
  return ['get-guest-sale-total', input];
}

export function getGuestTopKey(input?: getGuestInput) {
  return ['get-tops', input?.id];
}

export function getGuestUpcomingTripsKey(input?: getGuestInput) {
  return ['get-upcoming', input?.id];
}

export function getGuestCustomerRelationsKey(input?: getGuestInput) {
  return ['get-customer-relations', input?.id];
}

export function getGuestLastTransactionKey(input?: getGuestInput) {
  return ['get-latest-transactions', input?.id];
}

export function getGuestTopBookingPreferencesKey(input?: getGuestInput) {
  return ['get-top-booking-preferences', input?.id];
}

export function getGuestRelationTypeKey(input?: getGuestInput) {
  return ['get-guest-relation-type', input?.id];
}

export function getGuestTableKey(input?: getGuestInput) {
  return ['get-guest-table', input?.id];
}
